import { Component, ElementRef, HostListener, Input, ViewChild, forwardRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { COUNTRIES, CountryOption } from '../../data/countries';
import { splitInternationalPhone } from '../../utils/phone.util';
import { GeoService } from '../../../core/services/geo.service';

@Component({
    selector: 'app-phone-input',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './phone-input.component.html',
    styleUrls: ['./phone-input.component.scss'],
providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneInputComponent),
            multi: true
        }
    ]
})
export class PhoneInputComponent implements ControlValueAccessor, OnInit {
    private static readonly DEFAULT_COUNTRY_CODE = 'EG';

    @Input() label = 'Mobile';
    @Input() placeholder = '5xxxxxxx';
    @Input() hideLabel = false;
    @Input() disabled = false;

    @ViewChild('countrySearch') private countrySearchInput?: ElementRef<HTMLInputElement>;

    countries: CountryOption[] = COUNTRIES;

    value = { countryCode: PhoneInputComponent.DEFAULT_COUNTRY_CODE, phone: '' };
    countryMenuOpen = false;
    countrySearchQuery = '';
    private autoAttempted = false;
    private hasUserValue = false;
    private onChangeFn: (val: any) => void = () => {};
    private onTouchedFn: () => void = () => {};

    constructor(
        private hostRef: ElementRef<HTMLElement>,
        private geoService: GeoService
    ) {}

    ngOnInit(): void {
        this.detectCountry();
    }

    writeValue(obj: any): void {
        if (obj && typeof obj === 'object') {
            const raw = String(obj.countryCode || '').trim().toUpperCase();
            const normalized = raw && raw.length === 2 ? raw : PhoneInputComponent.DEFAULT_COUNTRY_CODE;
            this.value = { countryCode: normalized, phone: obj.phone || '' };
            // Treat non-default values as explicit selections (ex: existing user profile).
            this.hasUserValue = normalized !== PhoneInputComponent.DEFAULT_COUNTRY_CODE;
        } else {
            this.value = { countryCode: PhoneInputComponent.DEFAULT_COUNTRY_CODE, phone: '' };
            this.hasUserValue = false;
        }
        this.closeCountryMenu();
    }

    registerOnChange(fn: any): void {
        this.onChangeFn = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouchedFn = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = Boolean(isDisabled);
        if (this.disabled) {
            this.closeCountryMenu();
        }
    }

    get selectedCountry(): CountryOption {
        const code = String(this.value.countryCode || '').toUpperCase();
        return (
            this.countries.find((c) => c.code === code)
            || this.countries.find((c) => c.code === PhoneInputComponent.DEFAULT_COUNTRY_CODE)
            || this.countries[0]
            || { code: 'EG', name: 'Egypt', dial_code: '+20', flag: '🇪🇬' }
        );
    }

    get filteredCountries(): CountryOption[] {
        const q = String(this.countrySearchQuery || '').trim().toLowerCase();
        if (!q) return this.countries;
        const compact = q.replace(/\s+/g, '');
        return this.countries.filter((c) => {
            const name = c.name.toLowerCase();
            const code = c.code.toLowerCase();
            const dial = String(c.dial_code || '').toLowerCase();
            return name.includes(q) || code.includes(compact) || dial.includes(compact);
        });
    }

    trackByCountryCode(_idx: number, c: CountryOption): string {
        return c.code;
    }

    toggleCountryMenu(): void {
        if (this.disabled) return;
        if (this.countryMenuOpen) {
            this.closeCountryMenu();
            return;
        }
        this.countryMenuOpen = true;
        this.countrySearchQuery = '';
        setTimeout(() => this.countrySearchInput?.nativeElement?.focus(), 0);
    }

    closeCountryMenu(): void {
        this.countryMenuOpen = false;
        this.countrySearchQuery = '';
    }

    onCountrySearch(value: string): void {
        this.countrySearchQuery = String(value || '');
    }

    selectCountry(code: string): void {
        this.onCountryChange(code);
        this.closeCountryMenu();
    }

    onCountryChange(code: string) {
        if (this.disabled) return;
        this.hasUserValue = true;
        this.value = { ...this.value, countryCode: code };
        this.onChangeFn(this.value);
    }

    onPhoneChange(phone: string) {
        if (this.disabled) return;
        // A pasted/typed international value ("+201006541383", "00201006541383")
        // carries its own country code. Move it into the country selector and keep
        // only the national part, otherwise the consumer prefixes the selected
        // dial code again and produces numbers like +20201006541383 — which never
        // match the existing user row and so create duplicate users/leads.
        const international = splitInternationalPhone(phone);
        if (international && international.phone) {
            this.hasUserValue = true;
            this.value = { countryCode: international.countryCode, phone: international.phone };
            this.onChangeFn(this.value);
            return;
        }
        this.value = { ...this.value, phone };
        this.onChangeFn(this.value);
    }

    onTouched() {
        this.onTouchedFn();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.countryMenuOpen) return;
        const target = event.target as Node | null;
        if (!target) return;
        if (!this.hostRef.nativeElement.contains(target)) {
            this.closeCountryMenu();
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (!this.countryMenuOpen) return;
        this.closeCountryMenu();
    }

    private detectCountry() {
        if (this.autoAttempted || this.hasUserValue) return;
        this.autoAttempted = true;

        const current = String(this.value.countryCode || '').trim().toUpperCase();
        if (current && current !== PhoneInputComponent.DEFAULT_COUNTRY_CODE) return;

        void this.geoService
            .getCountryFromIp()
            .then(({ countryCode }) => {
                if (this.hasUserValue) return;
                const code = String(countryCode || '').trim().toUpperCase();
                if (!code || code.length !== 2) return;
                const match = this.countries.find(c => c.code === code);
                if (!match) return;
                if (match.code === this.value.countryCode) return;
                this.value = { ...this.value, countryCode: match.code };
                this.onChangeFn(this.value);
            })
            .catch(() => {
                // ignore failure; keep default
            });
    }
}
